import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { ConferenceList } from '../components/ConferenceList';
import { ErrorBlock, LoadingBlock } from '../components/states';
import { StatCard } from '../components/StatCard';
import { TileIcon } from '../components/TileIcon';
import {
  countdownTo,
  formatDateRange,
  formatShortDay,
  formatTime,
  formatTimeRange,
  hourIn,
} from '../lib/datetime';
import { currentDay, eventPhase, isHappeningNow, nextUp } from '../lib/eventPhase';
import {
  useAnnouncements,
  useEvent,
  useProgram,
  type PublicEvent,
  type Session,
} from '../lib/queries';
import { sessionPath } from '../lib/paths';
import { useReadAnnouncements } from '../lib/readAnnouncements';
import { useSavedSessions } from '../lib/savedSessions';
import { useSession } from '../lib/session';
import { groupTiles, LOCKED_NOTE, ROUTES, tileKey, type Tile } from '../lib/tiles';
import { useNow } from '../lib/useNow';

/** The sand bands the brand kit puts behind "Right now" and "The conference". */
const BAND = 'px-gutter border-y border-border-3 bg-sand py-5';

export function HomePage() {
  const event = useEvent();
  const now = useNow(event.data?.serverTime);
  const saved = useSavedSessions();
  const today = event.data ? currentDay(event.data, now) : null;

  // Only the current day is fetched, and only during the event: outside it there is no "now".
  const program = useProgram(today ? { day: today.key } : {}, { enabled: today !== null });

  if (event.isPending) {
    return <LoadingBlock label="Loading the conference…" />;
  }

  if (event.isError || !event.data) {
    return (
      <ErrorBlock
        message="The conference could not be loaded."
        onRetry={() => void event.refetch()}
      />
    );
  }

  const phase = eventPhase(event.data, now);
  const dayNumber = today ? event.data.days.findIndex((day) => day.key === today.key) + 1 : null;
  const sessions = today
    ? (program.data?.days.find((day) => day.key === today.key)?.sessions ?? [])
    : [];

  const { personal, conference, announcements } = groupTiles(event.data.tiles);

  return (
    <>
      <Greeting event={event.data} now={now} dayNumber={dayNumber} dayDate={today?.date ?? null} />

      <main>
        <section className={BAND}>
          <SectionLabel
            meta={
              dayNumber
                ? `Day ${dayNumber} · ${formatTime(now.toISOString(), event.data.timezone)}`
                : undefined
            }
          >
            {phase === 'during' ? 'Right now' : phase === 'before' ? 'Counting down' : 'Afterwards'}
          </SectionLabel>

          <div className="space-y-card">
            <Focus
              event={event.data}
              sessions={sessions}
              now={now}
              phase={phase}
              isLoading={program.isPending && today !== null}
              isError={program.isError && today !== null}
              dayKey={today?.key ?? null}
            />

            {announcements && <AnnouncementRow tile={announcements} />}
          </div>
        </section>

        <section className="px-gutter py-6">
          <SectionLabel>Yours</SectionLabel>

          <ul className="gap-card grid grid-cols-2">
            {personal.map((tile) => {
              const key = tileKey(tile);
              return (
                <li key={tile.id}>
                  <StatCard
                    iconKey={tile.iconKey}
                    label={tile.label}
                    subtitle={tile.subtitle}
                    // The starred count is the one personal number that exists before accounts do.
                    value={key === 'schedule' ? saved.ids.size : undefined}
                    to={key ? ROUTES[key] : undefined}
                    note={(key && LOCKED_NOTE[key]) ?? 'Arrives with your account'}
                  />
                </li>
              );
            })}

            <li>
              <StatCard
                iconKey="agenda"
                label="Agenda"
                value={today ? event.data.stats.sessionsToday : event.data.stats.sessions}
                subtitle={today ? 'Sessions today' : 'On the program'}
                to={today ? `/program?day=${today.key}` : '/program'}
              />
            </li>
          </ul>
        </section>

        {/* An organizer who curated no conference tiles gets no band, rather than a heading with
            nothing under it. */}
        {conference.length > 0 && (
          <section className={BAND}>
            <SectionLabel>The conference</SectionLabel>
            <ConferenceList tiles={conference} event={event.data} />
          </section>
        )}
      </main>
    </>
  );
}

function SectionLabel({ children, meta }: { children: ReactNode; meta?: string }) {
  return (
    <div className="mb-3 flex items-baseline justify-between gap-2">
      <h2 className="text-eyebrow tracking-eyebrow truncate font-bold text-ink-3 uppercase">
        {children}
      </h2>
      {meta && <p className="text-subtitle shrink-0 text-ink-3 tabular-nums">{meta}</p>}
    </div>
  );
}

interface GreetingProps {
  event: PublicEvent;
  now: Date;
  dayNumber: number | null;
  dayDate: string | null;
}

/** Time of day at the venue, not on the device: the greeting has to match the room. */
function Greeting({ event, now, dayNumber, dayDate }: GreetingProps) {
  const { session } = useSession();
  const hour = hourIn(event.timezone, now);
  const partOfDay = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const greeting = session ? `${partOfDay}, ${session.firstName}` : partOfDay;
  const countdown = countdownTo(event.startsOn, event.timezone, now);

  const pill =
    dayNumber && dayDate
      ? `Day ${dayNumber} · ${formatShortDay(dayDate, event.timezone)}`
      : countdown.days > 0
        ? `${countdown.days} day${countdown.days === 1 ? '' : 's'} to go`
        : null;

  return (
    <div className="px-gutter flex flex-wrap items-center justify-between gap-2 bg-surface pt-4 pb-5">
      <p className="text-greeting font-medium text-ink">{greeting}</p>
      {pill && (
        <p className="text-chip inline-flex shrink-0 items-center gap-2 rounded-full bg-sand-2 px-3 py-2 font-semibold text-ink-2">
          <span className="size-2 rounded-full bg-brand" aria-hidden="true" />
          {pill}
        </p>
      )}
    </div>
  );
}

interface FocusProps {
  event: PublicEvent;
  sessions: Session[];
  now: Date;
  phase: 'before' | 'during' | 'after';
  isLoading: boolean;
  isError: boolean;
  dayKey: string | null;
}

/**
 * The one card that answers "what do I do next" (HOME-02/06). It is the only dark surface and
 * the only shadow in the app, so whatever it holds is what the attendee should look at first.
 */
function Focus({ event, sessions, now, phase, isLoading, isError, dayKey }: FocusProps) {
  if (phase === 'before') {
    const countdown = countdownTo(event.startsOn, event.timezone, now);
    return (
      <FocusCard
        label="Starts in"
        title={
          countdown.days > 0
            ? `${countdown.days} day${countdown.days === 1 ? '' : 's'}`
            : `${countdown.hours}h ${countdown.minutes}m`
        }
        meta={formatDateRange(event.startsOn, event.endsOn, event.timezone)}
        primary={{ to: '/program', label: 'See the agenda' }}
        secondary={{ to: '/speakers', label: 'Speakers' }}
      />
    );
  }

  if (phase === 'after') {
    return (
      <FocusCard
        label="That’s a wrap"
        title="Thank you for joining us"
        meta="The program stays available here."
        primary={{ to: '/program', label: 'Browse the program' }}
      />
    );
  }

  if (isLoading) {
    return <QuietCard>Checking today’s program…</QuietCard>;
  }

  // A day that could not be fetched is not a day with nothing on it, and this card is the one
  // place the difference matters: "nothing further scheduled" would send somebody home.
  if (isError) {
    return (
      <QuietCard>
        Today’s program could not be loaded.{' '}
        <Link to="/program" className="font-semibold text-brand-text underline">
          Try the full agenda
        </Link>
      </QuietCard>
    );
  }

  const live = sessions.filter((session) => isHappeningNow(session, now));
  const upcoming = nextUp(sessions, now);
  const session = live.at(0) ?? upcoming;

  if (!session) {
    return (
      <QuietCard>
        Nothing further scheduled today.{' '}
        <Link to="/program" className="font-semibold text-brand-text underline">
          See the full agenda
        </Link>
      </QuietCard>
    );
  }

  const isLive = live.length > 0;
  const next = live.length > 0 ? upcoming : null;

  return (
    <FocusCard
      label={isLive ? 'Happening now' : 'Next up'}
      isLive={isLive}
      title={session.title}
      meta={[session.roomName, formatTimeRange(session.startsAt, session.endsAt, event.timezone)]
        .filter(Boolean)
        .join(' · ')}
      primary={{ to: sessionPath(session), label: 'Open session' }}
      secondary={
        next
          ? { to: sessionPath(next), label: 'What’s next' }
          : { to: dayKey ? `/program?day=${dayKey}` : '/program', label: 'Full agenda' }
      }
      footnote={
        live.length > 1
          ? `And ${live.length - 1} more happening now`
          : session.isCancelled
            ? (session.cancellationReason ?? 'This session is cancelled')
            : null
      }
    />
  );
}

interface Action {
  to: string;
  label: string;
}

function FocusCard({
  label,
  title,
  meta,
  primary,
  secondary,
  footnote,
  isLive = false,
}: {
  label: string;
  title: string;
  meta?: string | null;
  primary: Action;
  secondary?: Action;
  footnote?: string | null;
  isLive?: boolean;
}) {
  return (
    <article className="rounded-anchor shadow-float bg-ink p-5 text-white">
      <p className="text-eyebrow tracking-eyebrow flex items-center gap-2 font-bold text-brand-light uppercase">
        {/* The one bright green in the app: a live status on the black card, nowhere else. */}
        <span
          className={`size-2 shrink-0 rounded-full ${isLive ? 'bg-brand-on-dark' : 'bg-brand-light'}`}
          aria-hidden="true"
        />
        {label}
      </p>

      <h3 className="font-display text-screen-title mt-3 font-semibold">{title}</h3>
      {meta && <p className="text-subtitle mt-2 text-white/70">{meta}</p>}

      {/* Both actions share the width evenly and never wrap: on a 320px screen the labels
          would otherwise break mid-phrase inside the button. */}
      <div className="mt-5 flex gap-2">
        <Link
          to={primary.to}
          className="text-button rounded-button flex min-h-touch flex-1 basis-0 items-center justify-center bg-surface px-3 font-semibold whitespace-nowrap text-ink"
        >
          {primary.label}
        </Link>
        {secondary && (
          <Link
            to={secondary.to}
            className="text-button rounded-button flex min-h-touch flex-1 basis-0 items-center justify-center border border-white/25 px-3 font-semibold whitespace-nowrap text-white"
          >
            {secondary.label}
          </Link>
        )}
      </div>

      {footnote && <p className="text-subtitle mt-4 text-white/60">{footnote}</p>}
    </article>
  );
}

function QuietCard({ children }: { children: ReactNode }) {
  return (
    <p className="rounded-card text-body border border-border bg-surface p-5 text-ink-3">
      {children}
    </p>
  );
}

/**
 * HOME-03: the newest announcement said out loud on the home screen, with the unread count beside
 * it. The row keeps its shape when there is nothing to show — an organizer who put it there should
 * see it whether or not anything has been sent, and whether or not the feed can be read at all.
 */
function AnnouncementRow({ tile }: { tile: Tile }) {
  const { isRead } = useReadAnnouncements();
  const feed = useAnnouncements();

  const announcements = feed.data?.announcements ?? [];
  const latest = announcements.at(0);
  const unread = announcements.filter((announcement) => !isRead(announcement.id)).length;

  const icon = (
    <span className="rounded-well flex size-9 shrink-0 items-center justify-center bg-brand-wash text-brand-text">
      <TileIcon iconKey={tile.iconKey ?? 'announcements'} size={19} />
    </span>
  );

  if (!latest) {
    return (
      <div className={ANNOUNCEMENT_ROW} aria-disabled="true">
        {icon}
        <span className="min-w-0 flex-1">
          <span className="text-row block truncate font-semibold text-ink">{tile.label}</span>
          <span className="text-subtitle block text-ink-3">
            {feed.isPending
              ? 'Checking for news…'
              : feed.isError
                ? 'Announcements could not be loaded'
                : 'Nothing announced yet'}
          </span>
        </span>
      </div>
    );
  }

  return (
    <Link to="/announcements" className={ANNOUNCEMENT_ROW}>
      {icon}
      <span className="min-w-0 flex-1">
        <span className="text-row block truncate font-semibold text-ink">{latest.title}</span>
        <span className="text-subtitle block text-ink-3">{tile.label}</span>
      </span>
      {unread > 0 && (
        <span className="text-chip flex size-6 shrink-0 items-center justify-center rounded-full bg-brand font-bold text-white tabular-nums">
          {unread}
          <span className="sr-only"> unread</span>
        </span>
      )}
    </Link>
  );
}

const ANNOUNCEMENT_ROW =
  'rounded-card flex min-h-row items-center gap-3 border border-border bg-surface px-4 py-3';
